import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { GoogleMap, Marker } from '@react-google-maps/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { MapPin, Clock, Camera, Navigation, ExternalLink } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const mapContainerStyle = {
  width: '100%',
  height: '300px',
  borderRadius: '12px',
};

function SharedAssignment() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAssignment = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/share/${token}`);
        setData(response.data);
      } catch (err) {
        if (err.response?.status === 404 || err.response?.status === 410) {
          setError('This link has expired or been revoked. Please contact your coordinator for a new link.');
        } else if (err.response?.status === 403) {
          setError('This link is no longer active. Please request a new one from your coordinator.');
        } else {
          setError('Something went wrong loading your assignment. Please try again later.');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchAssignment();
  }, [token]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-500 mx-auto mb-4"></div>
          <p className="text-slate-500 text-sm">Loading your assignment...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50 px-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <MapPin className="w-8 h-8 text-red-500" />
            </div>
            <h2 className="text-lg font-semibold text-slate-800 mb-2">Link Unavailable</h2>
            <p className="text-slate-500 text-sm">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { event, photographer, spots } = data;

  // Sort spots by arrive_time
  const sortedSpots = [...spots].sort((a, b) => {
    const timeA = a.photographer_times?.arrive_time || a.arrive_time || '';
    const timeB = b.photographer_times?.arrive_time || b.arrive_time || '';
    return timeA.localeCompare(timeB);
  });

  // Calculate map center from spots
  const spotsWithCoords = spots.filter(s => s.lat && s.lng);
  const mapCenter = spotsWithCoords.length > 0
    ? {
        lat: spotsWithCoords.reduce((sum, s) => sum + s.lat, 0) / spotsWithCoords.length,
        lng: spotsWithCoords.reduce((sum, s) => sum + s.lng, 0) / spotsWithCoords.length,
      }
    : { lat: -23.55, lng: -46.63 };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  };

  const formatTime = (time) => {
    if (!time) return '';
    const [h, m] = time.split(':');
    const hour = parseInt(h);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const h12 = hour % 12 || 12;
    return `${h12}:${m} ${ampm}`;
  };

  const getDirectionsUrl = (address, lat, lng) => {
    if (lat && lng) {
      return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
    }
    return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-lg mx-auto px-4 py-4">
          <h1 className="text-xl font-bold text-slate-900">{event.name}</h1>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-sm text-slate-500">
            <span>{formatDate(event.date)}</span>
            {(event.start_time || event.end_time) && (
              <span className="flex items-center gap-1">
                <Clock className="w-3.5 h-3.5" />
                {formatTime(event.start_time)}{event.end_time ? ` - ${formatTime(event.end_time)}` : ''}
              </span>
            )}
          </div>
          <p className="text-sm text-emerald-600 font-medium mt-1">
            <Camera className="w-3.5 h-3.5 inline mr-1" />
            {photographer.name}
          </p>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-4 space-y-4">
        {/* Map */}
        {spotsWithCoords.length > 0 && (
          <Card>
            <CardContent className="p-2">
              <GoogleMap
                mapContainerStyle={mapContainerStyle}
                center={mapCenter}
                zoom={spotsWithCoords.length === 1 ? 15 : 13}
                options={{
                  disableDefaultUI: true,
                  zoomControl: true,
                  mapTypeControl: false,
                  streetViewControl: false,
                  fullscreenControl: false,
                }}
              >
                {sortedSpots.map((spot, index) => {
                  if (!spot.lat || !spot.lng) return null;
                  return (
                    <Marker
                      key={spot.id}
                      position={{ lat: spot.lat, lng: spot.lng }}
                      label={{
                        text: String(index + 1),
                        color: '#ffffff',
                        fontWeight: 'bold',
                        fontSize: '12px',
                      }}
                    />
                  );
                })}
              </GoogleMap>
            </CardContent>
          </Card>
        )}

        {/* Spot List */}
        <div className="space-y-3">
          <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide px-1">
            Your Spots ({sortedSpots.length})
          </h2>

          {sortedSpots.map((spot, index) => {
            const arriveTime = spot.photographer_times?.arrive_time || spot.arrive_time;
            const leaveTime = spot.photographer_times?.leave_time || spot.leave_time;

            return (
              <Card key={spot.id} className="overflow-hidden">
                <CardContent className="p-4">
                  {/* Spot name + number */}
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="flex items-center justify-center w-6 h-6 rounded-full bg-emerald-500 text-white text-xs font-bold shrink-0">
                        {index + 1}
                      </span>
                      <h3 className="font-semibold text-slate-900">{spot.name}</h3>
                    </div>
                    {spot.coverage_role && (
                      <Badge variant="secondary" className="text-xs shrink-0">
                        {spot.coverage_role}
                      </Badge>
                    )}
                  </div>

                  {/* Address + Directions */}
                  {spot.address && (
                    <a
                      href={getDirectionsUrl(spot.address, spot.lat, spot.lng)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800 mb-2"
                    >
                      <Navigation className="w-3.5 h-3.5 shrink-0" />
                      <span className="truncate">{spot.address}</span>
                      <ExternalLink className="w-3 h-3 shrink-0" />
                    </a>
                  )}

                  {/* Times */}
                  {(arriveTime || leaveTime) && (
                    <div className="flex items-center gap-1.5 text-sm text-slate-600 mb-2">
                      <Clock className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                      <span>
                        {arriveTime && leaveTime
                          ? `${formatTime(arriveTime)} - ${formatTime(leaveTime)}`
                          : formatTime(arriveTime || leaveTime)
                        }
                      </span>
                    </div>
                  )}

                  {/* Required Gear */}
                  {spot.required_gear && spot.required_gear.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {(Array.isArray(spot.required_gear) ? spot.required_gear : [spot.required_gear]).map((gear, i) => (
                        <Badge key={i} variant="outline" className="text-xs font-normal">
                          <Camera className="w-3 h-3 mr-1" />
                          {typeof gear === 'object' ? gear.name : gear}
                        </Badge>
                      ))}
                    </div>
                  )}

                  {/* Reference Images */}
                  {spot.reference_images && spot.reference_images.length > 0 && (
                    <div className="flex gap-2 mt-2 overflow-x-auto pb-1">
                      {spot.reference_images.map((img, i) => (
                        <img
                          key={i}
                          src={typeof img === 'object' ? img.url : img}
                          alt={`Reference ${i + 1}`}
                          className="w-20 h-20 object-cover rounded-lg border border-slate-200 shrink-0"
                        />
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Footer */}
        <div className="text-center py-6 text-xs text-slate-400">
          Powered by <span className="font-semibold text-slate-500">PhotoPlan</span>
        </div>
      </div>
    </div>
  );
}

export default SharedAssignment;
